import { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { AuthGate } from './auth/AuthGate.js';
import { AppCapabilityProvider } from './AppCapabilityProvider.js';
import { Reception } from './Reception.js';
import { History } from './History.js';
import { Notices } from './Notices.js';
import { Review } from './Review.js';
import { Labels } from './Labels.js';
import { Editor } from './restaurant/editor/Editor.js';
import { Printer } from './restaurant/printer/Printer.js';
import { Planning } from './team/Planning.js';
import { EffectifsPage } from './team/EffectifsPage.js';
import { NavMenu, NavLink } from './nav/NavMenu.js';
// ── The PMS group (operator, 2026-09-14) ───────────────────────────────────
//
// Three tabs, not the PMS: the rack, the room table and the payment log. All
// three are DESK work — « which room is free on Thursday », « how many beds in
// 214 », « the guest in 108 pays by card » — and all three are already within
// what `role = 'staff'` is granted (0057's C4 seed: pms.room.read,
// pms.reservation.read/write, pms.guest.read/write, pms.housekeeping.write;
// 20260923010100: pms.payment.read/write). Nothing here asks for a capability
// reception lacks — ADR-0019 T3's test, passed.
//
// Configuration is the one to watch, and it is safe because it already gates
// itself: its edit affordances hang off `useCapability(PMS_ROOM_WRITE)`, which
// `staff` does NOT hold, so the desk gets the 42-room table read-only and no
// button that would come back refused. That is the gate matching the RLS, not
// substituting for it (ARCHITECTURE §1).
//
// Clients joined 2026-09-29 (operator) — the cardex search and the fiche
// client (PMS-CARDEX-BRIEF §6.1). `staff` holds pms.guest.read/write, so the
// T3 test still passes; the fiche's identity half is read-only while Vega
// holds it, whoever opens it.
//
// Encaissement joined 2026-09-25 (operator) — the same component pms.* mounts,
// « Départs » sheet included. It is the one PMS surface the desk uses at every
// checkout, and the desk works from this app, not from pms.*. Its print sheet
// survives this app's `body * { visibility: hidden }`: pms.css reveals it with
// `body:has(.pms-print) .pms-print *`, which out-ranks `body *`.
//
// Tarifs joined 2026-10-18 (operator), in both cockpits and READ-ONLY at the
// desk — the Configuration precedent: every edit hangs off
// `useCapability(PMS_RATE_WRITE)`, which `staff` does not hold, and the RLS
// write policies require the same capability. The reads need `pms.rate.read`,
// which `staff` holds (0057). No Pages Function is called, so no origin to add.
//
// « Carte » joined 2026-10-19 (operator, till brief §16) — in the RESTAURANT
// group, not the PMS one: the till's catalogue is the salle's, beside « Menus ».
// Editable at the desk: `staff` holds `pms.catalogue.write` since
// 20261019230100; the extras catalogue stays under `pms.rate.write`.
//
// The rest of the PMS — Réservations, Calendrier, Occupation, Inventaire,
// Miroir Vega — stays at pms.lemontaigu.com. Same subset in bos.*.
//
// **The dependency direction now runs both ways** (`@lemontaigu/pms` already
// depends on this package for the nav/PWA shell). pnpm, esbuild and `tsc
// --noEmit` all resolve it, because every one of these is a source export and
// nothing is built ahead of anything else — but it is a cycle between two apps
// and it should not grow. The fix, when a third import wants to cross, is to
// lift the shell utilities (NavMenu, pwa/*, styles/index.css) into
// `packages/platform-ui`, where both apps can depend on them downward.
import { Rack } from '@lemontaigu/pms/Rack.js';
import { Configuration } from '@lemontaigu/pms/Configuration.js';
import { Clients } from '@lemontaigu/pms/Clients.js';
import { Encaissement } from '@lemontaigu/pms/Encaissement.js';
import { Tarifs } from '@lemontaigu/pms/Tarifs.js';
import { Carte } from '@lemontaigu/pms/Carte.js';
import { Inventaire } from '@lemontaigu/pms/Inventaire.js';
import { PerEtablissement, PmsProvider } from '@lemontaigu/pms/PmsProvider.js';
import '@lemontaigu/pms/styles/pms.css';
import { AppBrandingProvider } from './AppBrandingProvider.js';
import { AccountMenu, AppBrand, DeskOperatorChip } from '@lemontaigu/platform-ui';
import { AppDeskOperatorProvider } from './AppDeskOperatorProvider.js';
import { supabase } from './supabase.js';
import { disablePush, enablePush, getPushState, registerServiceWorker, type PushState } from './push.js';
import { hardReload, registerServiceWorker as registerSwUpdates, type SwUpdate } from './pwa/swUpdate.js';
import { InstallHint } from './pwa/InstallHint.js';
import './styles/index.css';
import './styles/editor.css';
import './styles/printer.css';
import './styles/team.css';
import './styles/fr-date.css';

// Nav reshuffle (operator, 2026-07-23): Conciergerie (ex Chat/En direct) and
// Planning (ex Équipe/team-planning) are now top-level links, not tucked
// inside dropdown groups. Équipe/Messages is parked (feature paused pending
// WhatsApp-habit training — see memory). Chat's remaining tabs regroup under
// Outils.
//
// Amended 2026-09-14: Restaurant is now Effectifs + Menus (Imprimer dropped
// from the nav, still routable); a PMS group joins with the rack and the room
// table. See the notes at RESTAURANT_TABS and at the PMS import site.
// Same auth as before (password + TOTP + public.staff).
type View =
  | 'editor'
  | 'printer'
  | 'live'
  | 'history'
  | 'notices'
  | 'review'
  | 'labels'
  | 'team-planning'
  | 'effectifs'
  | 'pmsPlan'
  | 'pmsClients'
  | 'pmsConfiguration'
  | 'pmsEncaissement'
  | 'pmsTarifs'
  | 'pmsInventaire'
  | 'carte';
const VIEWS: View[] = [
  'editor',
  'printer',
  'live',
  'history',
  'notices',
  'review',
  'labels',
  'team-planning',
  'effectifs',
  'pmsPlan',
  'pmsClients',
  'pmsConfiguration',
  'pmsEncaissement',
  'pmsTarifs',
  'pmsInventaire',
  'carte',
];
// Landing view, 2026-09-02: **Planning**, not Conciergerie. Now that the board
// is fed rather than typed, opening the app is how the desk finds out what the
// day looks like — arrivals, departures, who is on. The chat is a thing you go
// to when it pings; the board is the thing you check because you arrived.
const viewFromHash = (): View => {
  const hash = window.location.hash.replace('#', '');
  return (VIEWS as string[]).includes(hash) ? (hash as View) : 'team-planning';
};

// « Effectifs » leads the group (operator, 2026-09-14). It was a top-level link
// on the grounds that the salle and the kitchen carry the sheet every service
// and should not pay a click for it — true, and it is still one click, because
// it is now the FIRST item of the group it actually belongs to. What it costs
// is a hover; what it buys is that the restaurant's three surfaces read as one
// thing instead of one link and a menu.
//
// « Imprimer » is gone from the nav, NOT from the app: printing is reached by
// the button inside Menus (`onPrint`), which is where you are when you decide
// to print. The view stays routable — `#printer` still works, and the editor's
// button still goes there — it simply is not a destination you pick cold.
const RESTAURANT_TABS: { view: View; label: string }[] = [
  { view: 'effectifs', label: 'Effectifs' },
  { view: 'editor', label: 'Menus' },
  { view: 'carte', label: 'Carte' },
];
const PMS_TABS: { view: View; label: string }[] = [
  { view: 'pmsPlan', label: 'Planning' },
  { view: 'pmsClients', label: 'Clients' },
  { view: 'pmsConfiguration', label: 'Configuration' },
  { view: 'pmsEncaissement', label: 'Encaissement' },
  { view: 'pmsTarifs', label: 'Tarifs' },
  { view: 'pmsInventaire', label: 'Inventaire' },
];
const OUTILS_TABS: { view: View; label: string }[] = [
  { view: 'history', label: 'Historique' },
  { view: 'notices', label: 'Infos' },
  { view: 'review', label: 'Apprentissage' },
  { view: 'labels', label: 'Étiquettes' },
];

/** Web Push toggle for the on-duty phone (GC4). Hidden until VAPID is configured. */
function PushToggle() {
  const [state, setState] = useState<PushState>('unsupported');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void registerServiceWorker().then(() => getPushState()).then(setState);
  }, []);

  if (state === 'unsupported' || state === 'unconfigured') return null;
  if (state === 'denied') {
    return (
      <span className="app-push is-denied" title="Notifications bloquées dans le navigateur" aria-label="Notifications bloquées dans le navigateur">
        🔕
      </span>
    );
  }

  const toggle = async () => {
    setBusy(true);
    try {
      setState(state === 'on' ? await disablePush(supabase) : await enablePush(supabase));
    } catch {
      setState(await getPushState());
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      className={`app-push ${state === 'on' ? 'is-on' : ''}`}
      disabled={busy}
      title={state === 'on' ? 'Alertes activées' : 'Activer les alertes'}
      aria-label={state === 'on' ? 'Alertes activées' : 'Activer les alertes'}
      onClick={() => void toggle()}
    >
      {state === 'on' ? '🔔' : '🔕'}
    </button>
  );
}


/**
 * The desk header.
 *
 * CORRECTED at U4 (PMS-UNIFIED-DESK-BRIEF §2, §8). From 2026-09-14 this header
 * carried the établissement switcher, and the note here said the switcher was
 * what made `4AGUERES` reachable from the desk. It never was: the desk login
 * (`role:staff`) held `property HOTEL` only, so its list had one entry and the
 * menu showed only « Se déconnecter ». The switcher helped bos.* and pms.*, which
 * run on the operator's TENANT grant. The desk reaches the apartments from U4,
 * through the grant (`20261019200100`), and sees both buildings on the same
 * screens — there is nothing left to switch.
 */
function Nav({ view, onGo }: { view: View; onGo: (v: View) => void }) {
  return (
      <nav className="app-nav app-noprint">
        <AppBrand app="Réception" />
        {/* Order: Conciergerie · Planning · Restaurant · PMS · Outils
            (operator, 2026-09-14). « Planning » here is the TEAM board — the
            rota. The PMS group's « Planning » is the room rack. Both are called
            that on purpose: French borrowed the word back from English for both
            objects and the trade uses it for both, so the group name is what
            disambiguates, not the tab. */}
        <div className="app-groups">
          <NavLink label="Conciergerie" accent="#8fd19e" target="live" view={view} onGo={onGo} />
          <NavLink label="Planning" accent="#c9a0dc" target="team-planning" view={view} onGo={onGo} />
          <NavMenu label="Restaurant" accent="#e6b35a" tabs={RESTAURANT_TABS} view={view} onGo={onGo} />
          <NavMenu label="PMS" accent="#7a9e8e" tabs={PMS_TABS} view={view} onGo={onGo} />
          <NavMenu label="Outils" accent="#8fd19e" tabs={OUTILS_TABS} view={view} onGo={onGo} />
        </div>
        <button type="button" className="pwa-refresh-btn" title="Recharger l’application" onClick={hardReload}>
          🔄
        </button>
        <PushToggle />
        <DeskOperatorChip />
        <AccountMenu onSignOut={() => void supabase.auth.signOut()} />
      </nav>
  );
}

function App() {
  const [view, setView] = useState<View>(viewFromHash());
  const [swUpdate, setSwUpdate] = useState<SwUpdate | null>(null);

  useEffect(() => {
    const onHash = () => setView(viewFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    registerSwUpdates(setSwUpdate);
  }, []);

  const go = (v: View) => {
    window.location.hash = `#${v}`;
  };

  return (
    <div className="app">
      {/* `PmsProvider` wraps the SHELL, not just the PMS tabs (ADR-0019 §6,
          option 1).

          What this costs is one 2-row read of `app_property` per load, and a
          `<main>` that waits for it (the provider gates children on `!loading`
          so the first render already has a resolved scope and the
          per-établissement bundles). The header is NOT gated, so the app never
          looks blank — that is what `chrome` is for.

          Until U4 a switch of établissement remounted `<main>` — the chat and
          the board included — through the provider's key. The switcher and the
          key went with U4 (PMS-UNIFIED-DESK-BRIEF §8): the desk sees both
          buildings on the same screens, so nothing remounts them now. */}
      <PmsProvider
        chrome={
          <>
            {swUpdate && (
              <div className="update-banner app-noprint">
                <span>Nouvelle version disponible.</span>
                <button type="button" onClick={() => swUpdate.apply()}>
                  Recharger
                </button>
              </div>
            )}
            <InstallHint />
            <Nav view={view} onGo={go} />
          </>
        }
      >
      <main className="app-main">
        {view === 'editor' && <Editor onPrint={() => go('printer')} />}
        {view === 'printer' && <Printer />}
        {view === 'live' && <Reception />}
        {view === 'history' && <History />}
        {view === 'notices' && <Notices />}
        {view === 'review' && <Review />}
        {view === 'labels' && <Labels />}
        {view === 'team-planning' && <Planning />}
        {view === 'effectifs' && <EffectifsPage />}
        {view === 'pmsPlan' && <Rack />}
        {view === 'pmsClients' && <Clients />}
        {view === 'pmsConfiguration' && <Configuration />}
        {view === 'pmsEncaissement' && <Encaissement />}
        {view === 'pmsTarifs' && <PerEtablissement><Tarifs /></PerEtablissement>}
        {view === 'pmsInventaire' && <PerEtablissement><Inventaire /></PerEtablissement>}
        {view === 'carte' && <PerEtablissement><Carte /></PerEtablissement>}
      </main>
      </PmsProvider>
    </div>
  );
}

const el = document.getElementById('root');
if (el) {
  createRoot(el).render(
    <AuthGate>
      <AppBrandingProvider>
        <AppCapabilityProvider>
          <AppDeskOperatorProvider>
            <App />
          </AppDeskOperatorProvider>
        </AppCapabilityProvider>
      </AppBrandingProvider>
    </AuthGate>,
  );
}
